/**
 * AiChatPage — полноэкранный AI-помощник как отдельный раздел (/ai).
 *
 * Делит состояние беседы с модальной панелью (AiPanel) через общий aiStore:
 * одна и та же история сообщений, поэтому «Открыть как панель» продолжает тот
 * же разговор. Полноэкранный режим — основной; модалка остаётся как вариация
 * быстрого доступа с любой страницы.
 *
 * Композиция:
 *  - центрированная колонка беседы (макс. ширина для комфортного чтения);
 *  - пустое состояние — герой с подсказками (staggered reveal);
 *  - закреплённый снизу композер с мягким fade над ним;
 *  - переиспользует AiMessage / AiTyping / AiSuggestions / AiUploader / PulseAvatar.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Icon, IconButton, type IconName } from '@/shared/ui';
import { useAiStore, type AiAttachment } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { useConversations, useConversationActions } from '@/hooks/api/useConversations';
import { useAuth } from '@/auth/useAuth';
import { usePageHeader } from '@/components/layout/pageHeader';
import { AiMessage } from '@/components/ai/AiMessage';
import { AiTyping } from '@/components/ai/AiTyping';
import { PulseAvatar } from '@/components/ai/PulseAvatar';
import { AiSuggestions } from '@/components/ai/AiSuggestions';
import { AiUploader } from '@/components/ai/AiUploader';
import { toolCallsToSteps } from '@/components/ai/aiToolLabels';

const MAX_TEXTAREA_HEIGHT = 200;

const CAPABILITIES: { icon: IconName; text: string }[] = [
  { icon: 'Activity', text: 'Считаю CPL кросс-счётчиково' },
  { icon: 'Dashboard', text: 'Анализирую кампании и расход' },
  { icon: 'Hash', text: 'Собираю семантику через Wordstat' },
];

export function AiChatPage() {
  const { user } = useAuth();
  const streamingMessageId = useAiStore((s) => s.streamingMessageId);

  const { messages, isStreaming, awaitingFirstChunk, sendMessage, abort } = useAiChat();
  const { refresh: refreshConversations } = useConversationActions();

  const [input, setInput] = useState('');
  const [attached, setAttached] = useState<File[]>([]);
  const [showUploader, setShowUploader] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<string>('');

  const hasMessages = messages.length > 0;
  const firstName = useMemo(
    () => (user?.display_name ?? '').trim().split(/\s+/)[0] || '',
    [user?.display_name],
  );

  const streamingMessage = streamingMessageId
    ? messages.find((m) => m.id === streamingMessageId)
    : undefined;
  const thinkingSteps = streamingMessage
    ? toolCallsToSteps(
        streamingMessage.toolCalls,
        isStreaming,
        streamingMessage.content.length > 0,
      )
    : undefined;
  const noAnswerTextYet = (streamingMessage?.content.length ?? 0) === 0;
  const showThinking = isStreaming && (awaitingFirstChunk || noAnswerTextYet);

  usePageHeader({ title: 'AI-помощник', subtitle: 'Разговор с данными рекламы' });

  // После завершения стрима обновляем список истории (появился новый диалог).
  const prevStreaming = useRef(false);
  useEffect(() => {
    if (prevStreaming.current && !isStreaming) void refreshConversations();
    prevStreaming.current = isStreaming;
  }, [isStreaming, refreshConversations]);

  // Autosize textarea.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [input]);

  // Auto-scroll к низу при добавлении сообщения / росте стрима.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    const lastContent = messages[messages.length - 1]?.content ?? '';
    const grew = lastContent.length > lastMessageRef.current.length;
    lastMessageRef.current = lastContent;
    if (hasMessages) {
      requestAnimationFrame(() => {
        if (isNearBottom || grew || messages.length === 1) {
          el.scrollTop = el.scrollHeight;
        }
      });
    }
  }, [messages, hasMessages]);

  // Фокус на поле при входе в раздел.
  useEffect(() => {
    const t = window.setTimeout(() => textareaRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if ((!text && attached.length === 0) || isStreaming) return;

    let payloadOverride: string | undefined;
    const attachments: AiAttachment[] | undefined =
      attached.length > 0 ? attached.map((f) => ({ name: f.name, size: f.size })) : undefined;

    if (attached.length > 0) {
      const blocks: string[] = [text];
      for (const f of attached) {
        try {
          const content = await readFileText(f);
          blocks.push(`\n\n[Файл «${f.name}»]:\n\`\`\`\n${content.slice(0, 8000)}\n\`\`\``);
        } catch {
          blocks.push(`\n\n[Не удалось прочитать ${f.name}]`);
        }
      }
      payloadOverride = blocks.join('');
    }

    setInput('');
    setAttached([]);
    setShowUploader(false);

    await sendMessage(text, {
      ...(attachments ? { attachments } : {}),
      ...(payloadOverride ? { payloadOverride } : {}),
    });
  }, [input, attached, isStreaming, sendMessage]);

  // Стоп: рвём стрим, возвращаем последний запрос пользователя в поле для правки.
  // Частичная наработка ассистента остаётся в ленте (бейдж «Остановлено») и
  // уезжает в контекст следующего хода (бэкенд персистит её при обрыве).
  const handleStop = useCallback(() => {
    if (!isStreaming) return;
    abort();
    const lastUser = [...useAiStore.getState().messages]
      .reverse()
      .find((m) => m.role === 'user');
    const restore = lastUser?.content ?? '';
    setInput((cur) => (cur.trim() ? cur : restore));
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
  }, [isStreaming, abort]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (isStreaming) handleStop();
      else void handleSend();
    }
  };

  const onSuggestion = useCallback(
    async (text: string) => {
      if (isStreaming) return;
      setInput('');
      await sendMessage(text);
    },
    [isStreaming, sendMessage],
  );

  const sendDisabled = isStreaming || (!input.trim() && attached.length === 0);

  return (
    <div className="chat-shell">
      <ChatHistoryRail />
      <div className="ai-page">
      <div className="ai-page__glow" aria-hidden />

      <div ref={scrollRef} className="ai-page__scroll">
        <div className={'ai-page__col' + (hasMessages ? '' : ' is-empty')}>
          {!hasMessages ? (
            <div className="ai-page__hero">
              <div className="ai-page__hero-avatar">
                <PulseAvatar size={60} />
              </div>
              <div className="eyebrow ai-page__hero-eyebrow">AI-помощник · KOMMA Ads</div>
              <h1 className="ai-page__hero-title">
                {firstName ? `Чем помочь, ${firstName}?` : 'Чем помочь?'}
              </h1>
              <p className="ai-page__hero-sub">
                Спросите про CPL, кампании или расход, попросите собрать семантику или проверить
                аномалии — отвечу, запуская те же скиллы Яндекс.Директа, что и в Claude Code.
              </p>

              <div className="ai-page__caps">
                {CAPABILITIES.map((c) => (
                  <span key={c.text} className="ai-page__cap">
                    <Icon name={c.icon} size={15} />
                    <span>{c.text}</span>
                  </span>
                ))}
              </div>

              <div className="ai-page__suggestions">
                <AiSuggestions onSelect={(t) => void onSuggestion(t)} />
              </div>
            </div>
          ) : (
            <div className="col ai-page__thread">
              {messages.map((m) => {
                if (showThinking && m.id === streamingMessageId && m.content.length === 0) {
                  return null;
                }
                return <AiMessage key={m.id} message={m} />;
              })}
              {showThinking && <AiTyping steps={thinkingSteps} />}
            </div>
          )}
        </div>
      </div>

      <div className="ai-page__dock">
        <div className="ai-page__dock-inner">
          <AiUploader
            open={showUploader}
            onClose={() => setShowUploader(false)}
            onFiles={(files) => setAttached((prev) => [...prev, ...files])}
          />

          {attached.length > 0 && (
            <div className="row wrap gap6" style={{ marginBottom: 8 }}>
              {attached.map((f, i) => (
                <span
                  key={`${f.name}-${i}`}
                  className="chip"
                  style={{ background: 'var(--primary-100)', color: 'var(--primary-dark)' }}
                >
                  <Icon name="Paperclip" size={13} />
                  <span style={{ fontWeight: 600 }}>{f.name}</span>
                  <button
                    type="button"
                    className="ai-panel__chip-x"
                    onClick={() => setAttached((prev) => prev.filter((_, idx) => idx !== i))}
                    aria-label={`Убрать ${f.name}`}
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="composer ai-page__composer">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Спросите о рекламе…  (⌘ + Enter — отправить)"
              style={{ resize: 'none', minHeight: 52, maxHeight: MAX_TEXTAREA_HEIGHT, overflowY: 'auto' }}
            />
            <div className="composer__bar">
              <IconButton
                icon="Paperclip"
                small
                ghost
                tip="Прикрепить файл"
                onClick={() => setShowUploader((v) => !v)}
                aria-label="Прикрепить файл"
                style={showUploader ? { color: 'var(--primary)', borderColor: 'var(--primary)' } : undefined}
              />
              <div className="grow" />
              {isStreaming ? (
                <>
                  <span className="composer__send-hint muted" style={{ fontSize: 11.5, fontWeight: 600 }}>
                    Идёт ответ…
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    icon="Square"
                    onClick={handleStop}
                    aria-label="Остановить ответ"
                  >
                    Стоп
                  </Button>
                </>
              ) : (
                <>
                  <span className="composer__send-hint muted" style={{ fontSize: 11.5, fontWeight: 600 }}>
                    ⌘ + Enter
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    icon="Send"
                    onClick={() => void handleSend()}
                    disabled={sendDisabled}
                    aria-label="Отправить сообщение"
                  >
                    Отправить
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="ai-page__dock-foot muted">
            AI может ошибаться. Изменения боевого аккаунта применяются только после вашего апрува.
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

function ChatHistoryRail() {
  const conversationId = useAiStore((s) => s.conversationId);
  const newChat = useAiStore((s) => s.newChat);
  const isStreaming = useAiStore((s) => s.isStreaming);
  const { data: conversations } = useConversations();
  const { open, remove } = useConversationActions();

  return (
    <aside className="chat-rail">
      <div className="chat-rail__new">
        <button type="button" className="btn btn-outline btn-block btn-sm" onClick={newChat} disabled={isStreaming}>
          <Icon name="Plus" size={16} />
          <span>Новый чат</span>
        </button>
      </div>
      <div className="chat-rail__cap">Недавние</div>
      <div className="chat-rail__list">
        {!conversations?.length && (
          <div className="muted" style={{ padding: '8px 10px', fontSize: 13 }}>Пока пусто.</div>
        )}
        {conversations?.map((c) => (
          <div
            key={c.id}
            className={'chat-rail__item' + (c.id === conversationId ? ' active' : '')}
            onClick={() => void open(c.id)}
            title={c.title}
          >
            <span>{c.title}</span>
            <button
              type="button"
              className="chat-rail__del"
              aria-label="Удалить диалог"
              onClick={(e) => {
                e.stopPropagation();
                void remove(c.id);
              }}
            >
              <Icon name="Trash" size={14} />
            </button>
          </div>
        ))}
      </div>
    </aside>
  );
}

async function readFileText(file: File): Promise<string> {
  if (file.size > 5 * 1024 * 1024) {
    return `[Файл ${file.name} > 5MB — содержимое не приложено]`;
  }
  try {
    return await file.text();
  } catch {
    return `[Не удалось прочитать ${file.name}]`;
  }
}
